import { CountResponse } from 'app/apis/queryApi';
import { MetricReport } from 'features/reports/types/metric-report';
import {
  AxisChartByDate,
  ChartDataType,
  ChartDisplayType,
  DataPointSetByDate
} from '../charts/chart.models';
import { v4 as guid } from 'uuid';

export const formatDashboardSummaryChartData = (
  data: Record<string, CountResponse>
): MetricReport[] => {
  return Object.keys(data).reduce(
    (acc: MetricReport[], nextDataTypeKey: string) => {
      const dataValueDictionary = data[nextDataTypeKey];

      const dataValueArray = Object.keys(dataValueDictionary).map((dateKey) => {
        return {
          Field: dateKey,
          [nextDataTypeKey]: dataValueDictionary[dateKey]
        };
      });

      if (acc.length === 0) {
        acc.push(...dataValueArray);
        return acc;
      }

      dataValueArray.forEach((dataValue) => {
        const existingData = acc.find(
          (existingData) => existingData.Field === dataValue.Field
        );

        if (existingData === undefined) {
          throw new Error(
            'This should never happen, it means the data sent is missing the same number of days between DataTypes'
          );
        }

        existingData[nextDataTypeKey] = dataValue[nextDataTypeKey];
      });

      return acc;
    },
    []
  );
};

interface DataPointDateCount {
  dataSetName: string;
  dateCount: CountResponse;
}

export const toChartByDate = (
  visitors: CountResponse,
  web: CountResponse,
  inboundCall: CountResponse,
  cost: CountResponse,
  revenue: CountResponse,
  sales: CountResponse
): AxisChartByDate => {
  const dataPointDateCounts: DataPointDateCount[] = [
    { dataSetName: 'Visitors', dateCount: visitors },
    { dataSetName: 'Forms', dateCount: web },
    { dataSetName: 'Phone Calls', dateCount: inboundCall },
    { dataSetName: 'Cost', dateCount: cost },
    { dataSetName: 'Revenue', dateCount: revenue },
    { dataSetName: 'Sales', dateCount: sales }
  ];

  const dateStrings = getDateStrings(dataPointDateCounts);

  const dataPointSets = getDataPointSets(dataPointDateCounts, dateStrings);

  return {
    id: guid(),
    type: ChartDataType.DATE,
    title: '',
    shouldShowChartTypeDropdown: false,
    shouldShowDateRangeDropdown: true,
    dataPointSets,
    defaultChartDisplayType: ChartDisplayType.STACKED_BAR,
    xAxisTitle: '',
    yAxisTitle: '',
    yAxisFormat: new Intl.NumberFormat('en-GB'),
    tooltipFormat: new Intl.NumberFormat('en-GB')
  };

  function getDateStrings(dataPointDateCounts: DataPointDateCount[]): string[] {
    return [
      ...new Set(
        dataPointDateCounts
          .map((dataPointDateCount) =>
            Object.keys(dataPointDateCount.dateCount)
          )
          .flatMap((dateString) => dateString)
      )
    ];
  }

  function getDataPointSets(
    dataPointDateCounts: DataPointDateCount[],
    dateStrings: string[]
  ): DataPointSetByDate[] {
    return dataPointDateCounts.flatMap((dataPointSetCount) =>
      dateStrings.map((dateString) =>
        toDataPointSetByDate(dateString, dataPointSetCount)
      )
    );
  }

  function toDataPointSetByDate(
    dateString: string,
    dataPointSetCount: DataPointDateCount
  ): DataPointSetByDate {
    const dataPoint: DataPointSetByDate = {
      dataPointDate: dateString,
      countByDate: dataPointSetCount.dateCount[dateString] ?? 0,
      dataPointSetName: dataPointSetCount.dataSetName
    };
    return dataPoint;
  }
};
